import { FormApi } from "final-form";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "src/store";
import { PrimaryButton } from "src/ui/shared/CustomButton";
import { TextField } from "src/ui/shared/form/TextField";
import { Modal } from "src/ui/shared/Modal/Modal";

import { createGroup, setPaneTab, showDetails } from "@dashboard/devices/store";
import { Subscription } from "@dashboard/devices/types";
import { useCurrentWorkspace, useWorkspaceFeature } from "@dashboard/workspaces/api/helpers";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

import { selectGroups } from "../store/selectors";
import { getNestedGroupsWithIndentation, useParentGroupSelection, validateGroupForm } from "../utils";

interface FormValues {
    name: string;
    description: string;
    parentId: number | null;
}

interface Props {
    open: boolean;
    onClose: () => void;
    subscription?: Subscription;
}

const CreateGroupDialog = ({ open = false, ...props }: Props) => {
    const { t } = useTranslation(["modals", "common"]);
    const dispatch = useDispatch();

    const { data: workspace } = useCurrentWorkspace();
    const groups = useSelector(selectGroups);
    const groupNestingLimit = useWorkspaceFeature("GroupNestingLimit");
    const validate = (values: FormValues) => validateGroupForm(values, groups, groupNestingLimit);
    const { isLimitReached, handleParentGroupChange } = useParentGroupSelection(groups, groupNestingLimit);

    const groupOptions = getNestedGroupsWithIndentation(groups);

    const onSubmit = async (values: FormValues, form: FormApi<FormValues, Partial<FormValues>>) => {
        if (workspace) {
            await dispatch(createGroup({ subscriptionId: workspace.subscription.id, ...values }))
                .unwrap()
                .then((group) => {
                    if (group) {
                        dispatch(setPaneTab("OVERVIEW"));
                        dispatch(showDetails({ type: "group", selectedId: group.id }));
                    }
                    showSuccessToast(t("modals:createGroup.toast.groupCreatedSuccess"));
                    form.restart();
                    props.onClose();
                })
                .catch(({ message = t("modals:createGroup.toast.groupCreatedError") }) => {
                    showErrorToast(message);
                });
        }
    };
    const onKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>, form: FormApi<FormValues>) => {
        if (e.key === "Enter" && !form.getState().submitting) {
            e.preventDefault();
            await form.submit();
        }
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            validate={validate}
            initialValues={{ name: "", description: "", parentId: null }}
            render={({ handleSubmit, form }) => (
                <form onSubmit={handleSubmit}>
                    <Modal
                        open={open}
                        onClose={props.onClose}
                        title={t("modals:createGroup.title")}
                        onKeyDown={(e) => onKeyDown(e, form)}
                        endButton={
                            <PrimaryButton
                                onClick={() => form.submit()}
                                disabled={isLimitReached}
                                tooltipProps={{
                                    title: isLimitReached ? t("modals:createGroup.tooltip.limitReached") : "",
                                }}
                            >
                                {t("common:button.create")}
                            </PrimaryButton>
                        }
                    >
                        <TextField
                            name="name"
                            label={t("modals:createGroup.input.name.caption")}
                            placeholder={t("modals:createGroup.input.name.placeholder")}
                            fullWidth
                            required
                            autoFocus
                        />
                        <TextField
                            name="description"
                            label={t("common:label.description")}
                            placeholder={t("common:label.description")}
                            fullWidth
                        />
                        <ControlledSelectField
                            name="parentId"
                            label={t("modals:createGroup.input.parentGroup.caption")}
                            options={groupOptions}
                            onChange={handleParentGroupChange}
                            disableSorting
                            disableClearable={false}
                        />
                    </Modal>
                </form>
            )}
        />
    );
};

export default CreateGroupDialog;
